<template>
  <ComponentHeader title="User-Entity Links" subtitle="Which users are linked to which entities" />

  <div class="p-4">
    <!-- Search and Filter Bar -->
    <div class="bg-white p-4 rounded-lg border border-gray-100 mb-2">
      <div class="flex gap-4">
        <input
          v-model="searchQuery"
          placeholder="Search users..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        />
        <select
          v-model="filterType"
          class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        >
          <option value="all">All Users</option>
          <option value="with_entities">Users with Entities</option>
          <option value="without_entities">Users without Entities</option>
          <option value="inactive">Inactive Users</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="bg-white rounded-lg border border-gray-100 overflow-hidden mb-2">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Entity</th>
            <th class="px-6 py-3 w-32"></th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="n in 8" :key="n">
            <td class="px-6 py-4"><div class="h-4 w-36 bg-gray-200 rounded animate-pulse"></div></td>
            <td class="px-6 py-4"><div class="h-4 w-48 bg-gray-200 rounded animate-pulse"></div></td>
            <td class="px-6 py-4"><div class="h-5 w-24 bg-gray-100 rounded-full animate-pulse"></div></td>
            <td class="px-6 py-4"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Users Table -->
    <div v-else class="bg-white rounded-lg border border-gray-100 overflow-hidden mb-2">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
              @click="toggleSort('name')"
            >
              Name
              <span v-if="sortField === 'name'" class="ml-1">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
              @click="toggleSort('email')"
            >
              Email
              <span v-if="sortField === 'email'" class="ml-1">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Linked Entity
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="user in paginatedUsers" :key="user.id" :class="{ 'opacity-50': !user.is_active }">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium sensitive" :class="user.is_active ? 'text-gray-900' : 'text-gray-400'">
                {{ user.first_name }} {{ user.last_name }}
                <span v-if="!user.is_active" class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Inactive</span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 sensitive">
              {{ user.email }}
            </td>
            <td class="px-6 py-4">
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="entity in user.entities"
                  :key="entity.id"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 sensitive"
                >
                  <router-link
                    :to="`/entity/${entity.id}/`"
                    class="hover:underline sensitive"
                  >{{ entity.name }}</router-link>
                  <span class="text-blue-400 ml-1">({{ entity.entity_type__type_name }})</span>
                  <button
                    @click="promptUnlink(user, entity)"
                    class="ml-1 text-blue-400 hover:text-red-600"
                    title="Unlink user from this entity"
                  >
                    <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                  </button>
                </span>
                <span
                  v-if="user.entities.length === 0"
                  class="text-sm text-gray-400 italic"
                >
                  No entities assigned
                </span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button
                @click="openAssignModalForUser(user)"
                class="text-primary-500 hover:text-primary-700"
              >
                <font-awesome-icon :icon="['fas', 'link']" class="mr-1" />
                Link Entity
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="text-center py-12">
        <font-awesome-icon :icon="['fas', 'users']" class="text-4xl text-gray-300 mb-3" />
        <p class="text-gray-500">No users found</p>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div v-if="filteredUsers.length > 0" class="bg-white px-4 py-3 rounded-lg flex items-center justify-between border border-gray-100">
      <div class="flex-1 max-sm:flex justify-between sm:hidden">
        <button
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div class="max-sm:hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, filteredUsers.length) }}</span>
            of
            <span class="font-medium">{{ filteredUsers.length }}</span>
            users
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Previous</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
            <template v-for="page in totalPages" :key="page">
              <button
                v-if="page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)"
                @click="currentPage = page"
                :class="[
                  page === currentPage
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]"
              >
                {{ page }}
              </button>
              <span
                v-else-if="page === currentPage - 3 || page === currentPage + 3"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
              >
                ...
              </span>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Next</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <LinkEntityModal
      :is-visible="showAssignModal"
      :user-name="selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''"
      @close="closeAssignModal"
      @link="onLinkEntity"
    />

    <ConfirmationModal
      :is-visible="!!unlinkTarget"
      :message="unlinkMessage"
      @confirm="confirmUnlink"
      @cancel="cancelUnlink"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import { getUserEntityAssignments, unlinkEntityUser, assignEntityToUser } from '@/services/api/entities';
import LinkEntityModal from '@/components/Entity/LinkEntityModal.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faLink } from '@fortawesome/free-solid-svg-icons';

library.add(faTimes, faLink);

export default {
  name: 'UserEntityLinks',
  components: {
    FontAwesomeIcon,
    ComponentHeader,
    ConfirmationModal,
    LinkEntityModal
  },
  data() {
    return {
      userAssignments: [],
      searchQuery: '',
      filterType: 'all',
      showAssignModal: false,
      selectedUser: null,
      unlinkTarget: null,
      loading: false,
      currentPage: 1,
      itemsPerPage: 50,
      sortField: 'name',
      sortDirection: 'asc'
    };
  },
  computed: {
    unlinkMessage() {
      if (!this.unlinkTarget) return '';
      const { user, entity } = this.unlinkTarget;
      return `You are unlinking the entity "${entity.name}" (${entity.entity_type__type_name}) from ${user.first_name} ${user.last_name}. The entity will not be deleted — only the link between the user and entity is removed.`;
    },

    tenantUsers() {
      return this.userAssignments.filter(user =>
        !user.email?.toLowerCase().endsWith('@kaironhealth.com')
      );
    },

    filteredUsers() {
      let filtered = this.tenantUsers;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user => {
          return (
            user.first_name?.toLowerCase().includes(query) ||
            user.last_name?.toLowerCase().includes(query) ||
            user.username?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
          );
        });
      }

      if (this.filterType === 'with_entities') {
        filtered = filtered.filter(user => user.entities.length > 0);
      } else if (this.filterType === 'without_entities') {
        filtered = filtered.filter(user => user.entities.length === 0);
      } else if (this.filterType === 'inactive') {
        filtered = filtered.filter(user => !user.is_active);
      }

      const dir = this.sortDirection === 'asc' ? 1 : -1;
      filtered.sort((a, b) => {
        let aVal, bVal;
        if (this.sortField === 'name') {
          aVal = `${a.last_name} ${a.first_name}`.toLowerCase();
          bVal = `${b.last_name} ${b.first_name}`.toLowerCase();
        } else {
          aVal = (a[this.sortField] || '').toLowerCase();
          bVal = (b[this.sortField] || '').toLowerCase();
        }
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });

      return filtered;
    },

    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },

  },
  watch: {
    searchQuery() {
      this.currentPage = 1; // Reset to first page when searching
    },
    filterType() {
      this.currentPage = 1; // Reset to first page when filtering
    },
  },
  mounted() {
    this.fetchUserAssignments();
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },

    openAssignModalForUser(user) {
      this.selectedUser = user;
      this.showAssignModal = true;
    },

    closeAssignModal() {
      this.showAssignModal = false;
      this.selectedUser = null;
    },

    async onLinkEntity({ entity_type_id, existing_entity_id }) {
      try {
        const result = await assignEntityToUser({
          appuser_id: this.selectedUser.id,
          entity_type_id,
          existing_entity_id,
        });
        if (result.success) {
          await this.fetchUserAssignments();
          this.closeAssignModal();
        }
      } catch (error) {
        console.error('Error linking entity:', error);
        this.showError(error.response?.data?.error || 'Failed to link entity');
      }
    },

    async fetchUserAssignments() {
      this.loading = true;
      try {
        this.userAssignments = await getUserEntityAssignments();
      } catch (error) {
        console.error('Error fetching user assignments:', error);
        this.showError('Failed to load user assignments');
      } finally {
        this.loading = false;
      }
    },

    promptUnlink(user, entity) {
      this.unlinkTarget = { user, entity };
    },

    cancelUnlink() {
      this.unlinkTarget = null;
    },

    async confirmUnlink() {
      const { entity } = this.unlinkTarget;
      this.unlinkTarget = null;

      try {
        const result = await unlinkEntityUser(entity.id);
        if (result.success) {
          await this.fetchUserAssignments();
        }
      } catch (error) {
        console.error('Error unlinking entity:', error);
        this.showError(error.response?.data?.error || 'Failed to unlink entity');
      }
    },

    showError(message) {
      alert(message);
    }
  }
};
</script>
